import { useState } from 'react'
import { kycApi } from '../../../services/api-client'
import type { VendorKYC } from '../../../types'

interface Props {
  kyc: VendorKYC | null
  onComplete: (kyc: VendorKYC) => void
  onBack?: () => void
}

export function Step3Address({ kyc, onComplete, onBack }: Props) {
  const [addressLine1, setAddressLine1] = useState(kyc?.address_line1 || '')
  const [addressLine2, setAddressLine2] = useState(kyc?.address_line2 || '')
  const [city, setCity] = useState(kyc?.city || '')
  const [stateProvince, setStateProvince] = useState(kyc?.state_province || '')
  const [postalCode, setPostalCode] = useState(kyc?.postal_code || '')
  const [country, setCountry] = useState(kyc?.country || '')
  const [proofImage, setProofImage] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!proofImage) {
      setError('Debe subir un comprobante de domicilio')
      return
    }

    setError(null)
    setLoading(true)

    try {
      const formData = new FormData()
      formData.append('address_line1', addressLine1)
      if (addressLine2) formData.append('address_line2', addressLine2)
      formData.append('city', city)
      formData.append('state_province', stateProvince)
      formData.append('postal_code', postalCode)
      formData.append('country', country)
      formData.append('proof_of_address_image', proofImage)

      const response = await kycApi.submitStep3(formData)
      onComplete(response.kyc)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al guardar la dirección')
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Información de Dirección
        </h2>
        <p className="text-gray-600">
          Proporcione su dirección actual y un comprobante de domicilio reciente.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <div className="space-y-6">
        <div>
          <label htmlFor="address_line1" className="block text-sm font-medium text-gray-700 mb-2">
            Dirección Línea 1 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="address_line1"
            required
            value={addressLine1}
            onChange={(e) => setAddressLine1(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Calle Principal #123"
          />
        </div>

        <div>
          <label htmlFor="address_line2" className="block text-sm font-medium text-gray-700 mb-2">
            Dirección Línea 2 (opcional)
          </label>
          <input
            type="text"
            id="address_line2"
            value={addressLine2}
            onChange={(e) => setAddressLine2(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Apartamento, Piso, etc."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
              Ciudad <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="city"
              required
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Ciudad de México"
            />
          </div>

          <div>
            <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
              Estado/Provincia <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="state"
              required
              value={stateProvince}
              onChange={(e) => setStateProvince(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="CDMX"
            />
          </div>

          <div>
            <label htmlFor="postal_code" className="block text-sm font-medium text-gray-700 mb-2">
              Código Postal <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="postal_code"
              required
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="12345"
            />
          </div>

          <div>
            <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
              País <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="country"
              required
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="México"
            />
          </div>
        </div>

        <div>
          <label htmlFor="proof_image" className="block text-sm font-medium text-gray-700 mb-2">
            Comprobante de Domicilio <span className="text-red-500">*</span>
          </label>
          <input
            type="file"
            id="proof_image"
            required={!kyc?.proof_of_address_image}
            accept="image/*,application/pdf"
            onChange={(e) => setProofImage(e.target.files?.[0] || null)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="text-sm text-gray-500 mt-1">
            Recibo de servicios, estado de cuenta bancario o documento oficial (últimos 3 meses)
          </p>
        </div>
      </div>

      <div className="flex justify-between pt-6 border-t">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-3 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          >
            Atrás
          </button>
        )}
        <button
          type="submit"
          disabled={loading}
          className={`px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${!onBack ? 'ml-auto' : ''}`}
        >
          {loading ? 'Guardando...' : 'Continuar'}
        </button>
      </div>
    </form>
  )
}
